import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Badge } from 'react-bootstrap';
import { useAuth } from '../security/AuthContext';
import { apiClient } from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './UpdatePasswordComponent.css';

function UpdatePasswordComponent() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [requestCompleted, setRequestCompleted] = useState(false);
    const [errorDetails, setErrorDetails] = useState('');
    
    const navigate = useNavigate();
    const authContext = useAuth();
    const {fastPhotos, loading: fastPhotosLoading, error: fastPhotosError} = useFastPhotos();

    function handleCurrentPasswordChange(event) {
        setCurrentPassword(event.target.value);
    }

    function handleNewPasswordChange(event) {
        setNewPassword(event.target.value);
    }

    function handleConfirmPasswordChange(event) {
        setConfirmPassword(event.target.value);
    }

    // Handle click on a close match profile
    const handleCloseMatchClick = (e, memberId) => {
        e.preventDefault();
        const token = localStorage.getItem('token');

        if (token) {
            // Record the profile view
            recordProfileView(memberId);
            // Navigate to the profile page
            navigate(`/profile/view/1/${memberId}`);
        } else {
            navigate('/login');
        }
    };

    // Function to record profile view
    const recordProfileView = async (memberId) => {
        try {
            console.log('Recording view for profile ID:', memberId);
            
            const response = await apiClient.post('/rest/recordView', {
                viewedMemberId: memberId
            });
            
            console.log('View recorded response:', response.data);
        } catch (error) {
            console.error('Error recording profile view:', error);
        }
    };

    async function handleUpdatePassword() {
        // Reset error states
        setShowErrorMessage(false);
        setErrorDetails('');

        // Validate inputs
        if (!currentPassword) {
            setShowErrorMessage(true);
            setErrorDetails('Please enter your current password.');
            return;
        }

        if (!newPassword) {
            setShowErrorMessage(true);
            setErrorDetails('Please enter a new password.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setShowErrorMessage(true);
            setErrorDetails('New password and confirmation do not match.');
            return;
        }

        try {
            // Format request according to the expected backend structure
            const passwordRequest = {
                passwordChanges: [
                    {
                        name: "currentPassword",
                        val: currentPassword
                    },
                    {
                        name: "newPassword",
                        val: newPassword
                    },
                    {
                        name: "newPasswordCheck",
                        val: confirmPassword
                    }
                ]
            };
            
            // Call the new endpoint
            const response = await apiClient.post('/rest/profile/changepassword', passwordRequest);
            
            console.log('Password update response:', response.data);
            
            // Looking at your screenshot, the response is { passwordupdate: "pass" } for success
            if (response.status === 200 && 
                (response.data.passwordupdate === 'success' || response.data.passwordupdate === 'pass')) {
                setShowErrorMessage(false);
                setRequestCompleted(true);
                
                // Redirect to profile after a short delay
                setTimeout(() => {
                    navigate('/email/inbox');
                }, 3000);
            } else {
                setShowErrorMessage(true);
                setErrorDetails('Password update failed. Please check your current password and try again.');
                setRequestCompleted(false);
            }
        } catch (error) {
            console.error('Password update failed:', error);
            setShowErrorMessage(true);
            setErrorDetails('An error occurred while processing your request. Please try again later.');
            setRequestCompleted(false);
        }
    }

    return (
        <div className="container-1024">
            <div className="results-area">
                <div className="results-content">
                    <div className="results-center-details">
                        <div className="update-password-container">
                            <div className="update-password-box">
                                <h2 className="update-password-title">Manage personal details</h2>
                                
                                {!requestCompleted ? (
                                    <>
                                        <div className="update-password-subtitle">
                                            Update your password below:
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Current Password</label>
                                            <input
                                                type="password"
                                                placeholder="Enter your current password"
                                                className="form-input"
                                                value={currentPassword}
                                                onChange={handleCurrentPasswordChange}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">New Password</label>
                                            <input
                                                type="password"
                                                placeholder="Enter your new password"
                                                className="form-input"
                                                value={newPassword}
                                                onChange={handleNewPasswordChange}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label className="form-label">Confirm New Password</label>
                                            <input
                                                type="password"
                                                placeholder="Confirm your new password"
                                                className="form-input"
                                                value={confirmPassword}
                                                onChange={handleConfirmPasswordChange}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdatePassword();
                                                }}
                                            />
                                        </div>
                                        <button
                                            className="auth-button update-button"
                                            onClick={handleUpdatePassword}
                                        >
                                            Make updates
                                        </button>
                                        <button
                                            className="auth-button cancel-button"
                                            onClick={() => navigate('/profile')}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : null}
                                
                                {showErrorMessage && (
                                    <div className="error-message">
                                        {errorDetails || "Request error"}
                                    </div>
                                )}
                                
                                {requestCompleted && (
                                    <div className="success-message">
                                        Your password has been successfully updated. You will be redirected to your profile shortly.
                                    </div>
                                )}
                                
                                {!requestCompleted ? (
                                    <div className="action-links">
                                        <Link to="/forgot-password" className="forgot-link">Forgot my password</Link>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="results-right-sidebar">
                        <h5>Close Matches</h5>
                        {fastPhotosLoading ? (
                            <p>Loading...</p>
                        ) : fastPhotosError ? (
                            <p>{fastPhotosError}</p>
                        ) : fastPhotos && fastPhotos.length > 0 ? (
                            fastPhotos.map((photo, index) => (
                                <Card key={index} className="fast-photo-card-side">
                                    <a 
                                        href={`/profile/view/1/${photo[0]}`}
                                        onClick={(e) => handleCloseMatchClick(e, photo[0])}
                                    >
                                        <Card.Img 
                                            variant="top" 
                                            src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                                            }}
                                        />
                                    </a>
                                    <Badge pill bg="light" text="dark">
                                        {photo[2]}
                                    </Badge>
                                </Card>
                            ))
                        ) : (
                            <p>No Close Matches Found</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UpdatePasswordComponent;